import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useTableFilter } from '@/shared';
import {
  useFetchMasivos,
  usePausarMasivo,
  usePausarMasivoConArchivos,
  useReactivarMasivo,
} from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-masivos.actions';
import { ITicketMasivo } from '@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface';
import { ROUTER_PATHS } from '@/shared/constants/routes/router-paths.constants';
import TablaMasivos from '../components/TablaMasivos';
import PausarMasivoDialog from '../components/PausarMasivoDialog';

const ESTADO_OPTIONS = [
  { value: '', label: 'Todos', color: '' },
  { value: 'activo', label: 'Activo', color: '#f44336' },
  { value: 'pausado', label: 'Pausado', color: '#ffb300' },
  { value: 'en_reparacion', label: 'En Reparacion', color: '#ff9800' },
  { value: 'resuelto', label: 'Resuelto', color: '#4caf50' },
];

const MasivosPage: React.FC = () => {
  const navigate = useNavigate();

  const [estadoMasivo, setEstadoMasivo] = useState<string>('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  const { pagination, setPagination } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  const queryParams: Record<string, any> = {
    page: pageIndex + 1,
    page_size: pageSize,
  };
  if (estadoMasivo) queryParams.estado_masivo = estadoMasivo;
  if (fechaDesde) queryParams.fecha_creacion_after = fechaDesde;
  if (fechaHasta) queryParams.fecha_creacion_before = fechaHasta;

  const {
    data: masivosData,
    isLoading,
    isRefetching,
  } = useFetchMasivos(queryParams);

  const handleClearFilters = () => {
    setEstadoMasivo('');
    setFechaDesde('');
    setFechaHasta('');
  };

  const handleViewDetail = (masivo: ITicketMasivo) => {
    navigate(`${ROUTER_PATHS.ticketSoporteCliente.masivo_detalle_nav}/${masivo.id_ticket_masivo}`);
  };

  const handleResolve = (masivo: ITicketMasivo) => {
    navigate(`${ROUTER_PATHS.ticketSoporteCliente.masivo_detalle_nav}/${masivo.id_ticket_masivo}`);
  };

  // Pausar / Reactivar
  const [pausarTarget, setPausarTarget] = useState<ITicketMasivo | null>(null);
  const pausarMutation = usePausarMasivo();
  const pausarConArchivosMutation = usePausarMasivoConArchivos();
  const reactivarMutation = useReactivarMasivo();

  const handlePause = (masivo: ITicketMasivo) => setPausarTarget(masivo);
  const handleConfirmPause = (motivo: string, archivos: File[]) => {
    if (!pausarTarget) return;
    const onSettled = () => setPausarTarget(null);
    if (archivos.length > 0) {
      pausarConArchivosMutation.mutate(
        { id: pausarTarget.id_ticket_masivo, motivo, archivos },
        { onSettled },
      );
    } else {
      pausarMutation.mutate(
        { id: pausarTarget.id_ticket_masivo, motivo },
        { onSettled },
      );
    }
  };
  const handleReactivate = (masivo: ITicketMasivo) => {
    reactivarMutation.mutate({ id: masivo.id_ticket_masivo });
  };

  return (
    <Paper elevation={2} sx={{ padding: 2, mb: 2, borderRadius: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h4"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <WarningAmberIcon color="warning" />
          GESTION DE DANOS MASIVOS
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate(ROUTER_PATHS.ticketSoporteCliente.masivos_crear_nav)}
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          Crear Masivo
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Filtros */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={estadoMasivo}
                label="Estado"
                onChange={(e: SelectChangeEvent) => setEstadoMasivo(e.target.value)}
              >
                {ESTADO_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.color ? (
                      <Chip
                        label={opt.label}
                        size="small"
                        sx={{ bgcolor: opt.color, color: '#fff' }}
                      />
                    ) : (
                      opt.label
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Desde"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Hasta"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{ textTransform: 'none' }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Tabla */}
      <TablaMasivos
        data={(masivosData?.data as any)?.data ?? masivosData?.data ?? []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        pagination={pagination}
        onPaging={setPagination}
        rowCount={(masivosData?.data as any)?.count}
        onViewDetail={handleViewDetail}
        onResolve={handleResolve}
        onPause={handlePause}
        onReactivate={handleReactivate}
      />

      <PausarMasivoDialog
        open={!!pausarTarget}
        masivo={pausarTarget}
        isLoading={pausarMutation.isPending || pausarConArchivosMutation.isPending}
        onClose={() => setPausarTarget(null)}
        onConfirm={handleConfirmPause}
      />
    </Paper>
  );
};

export default MasivosPage;
